import { useMemo, useState } from 'react';
import { toApiError } from '@/api/client';
import { apiRatingToUi, uiRatingToApi, type UiRating } from '@/api/mappers';
import { useRateReading } from '@/hooks/api/useReadings';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { useToast } from '@/hooks/useToast';
import { ratingOptions, t } from '@/i18n';
import type { components } from '@/api/schema';
import styles from './RatingRow.module.css';

type ApiRating = components['schemas']['Rating'];

interface RatingRowProps {
  readingId: number;
  initialRating?: string | null;
}

function toUiRating(raw: string | null | undefined): UiRating | null {
  if (raw === 'good' || raw === 'neutral' || raw === 'bad') {
    return apiRatingToUi[raw as ApiRating];
  }
  return null;
}

export function RatingRow({ readingId, initialRating = null }: RatingRowProps) {
  const initial = useMemo(() => toUiRating(initialRating), [initialRating]);
  const [sel, setSel] = useState<UiRating | null>(initial);
  const { showToast } = useToast();
  const onApiError = useApiErrorToast();
  const rate = useRateReading();

  const handlePick = async (next: UiRating) => {
    if (rate.isPending) return;
    const prev = sel;
    setSel(next);
    try {
      await rate.mutateAsync({ readingId, rating: uiRatingToApi[next] });
      showToast(t.result.ratingThanks, 'success');
    } catch (err) {
      setSel(prev);
      // A 401 means the session died, not that rating failed — let the
      // "session expired" default win instead of masking it with a custom
      // fallback (same fix as SpreadPage's job-creation error handling).
      onApiError(
        err,
        toApiError(err).status === 401 ? undefined : t.result.ratingFailed,
      );
    }
  };

  return (
    <div className={styles.row}>
      <div className={styles.lbl}>{t.result.ratingPrompt}</div>
      <div className={styles.btns}>
        {ratingOptions.map((r) => (
          <button
            key={r.v}
            type="button"
            className={[styles.btn, sel === r.v ? styles.sel : ''].filter(Boolean).join(' ')}
            onClick={() => handlePick(r.v)}
            disabled={rate.isPending}
            aria-label={r.label}
            aria-pressed={sel === r.v}
          >
            {r.icon}
          </button>
        ))}
      </div>
    </div>
  );
}
